import { RefObject, useEffect, useRef } from 'react';

import { useQueryState } from 'nuqs';

import { useClientHasRendered } from '../hooks';
import { QueryStringKey } from '../url/queryUtils';

import { openPostViewerEventSources } from '../../components/PostsViewer/types/openPostViewerEventSources';
import { useScrollToElement } from './useScrollToElement';

interface IPostViewer {
  currentPostViewerId: string | undefined;
  closePostViewer: (postId?: string) => Promise<void>;
  isPostViewerOpen: boolean;
  openPostViewer: (postId?: string, eventSource?: openPostViewerEventSources) => Promise<void>;
  openPostViewerEventSource: openPostViewerEventSources | null;
  setCurrentPostViewerId: (postId: string) => Promise<void>;
}

const DEFAULT_SCROLL_OFFSET_FROM_TOP = 8;

export const usePostViewer = <E extends HTMLElement>(
  postsReference?: RefObject<E | null>,
  scrollOffsetFromTop = DEFAULT_SCROLL_OFFSET_FROM_TOP,
  useWindowToScroll = true,
): IPostViewer => {
  const clientHasRendered = useClientHasRendered();
  const [isOpen, setIsOpen] = useQueryState(QueryStringKey.PostViewerOpen, { history: 'push' });
  const [postId, setPostId] = useQueryState(QueryStringKey.CurrentPostViewerId);
  const [openPostViewerEventSource, setOpenPostViewerEventSource] = useQueryState(
    QueryStringKey.PostViewerEventSource,
    {
      parse: (query: string) => {
        const values = Object.values(openPostViewerEventSources);
        if (values.includes(query as unknown as openPostViewerEventSources)) {
          return query as openPostViewerEventSources;
        }
        return null;
      },
    },
  );
  const postIdReference = useRef<string | undefined>(undefined);

  useScrollToElement(postsReference, `[id="${postIdReference.current}"]`, {
    offsetFromTop: scrollOffsetFromTop,
    useWindow: useWindowToScroll,
  });

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const openPostViewer = async (openPostId?: string, eventSource?: openPostViewerEventSources) => {
    if (eventSource) {
      await setOpenPostViewerEventSource(eventSource, { scroll: false, shallow: true });
    }
    if (openPostId) {
      await setPostId(openPostId, { scroll: false, shallow: true });
    }
    await setIsOpen('true', { scroll: false, shallow: true });
  };

  const closePostViewer = async (postId?: string): Promise<void> => {
    if (postId) {
      postIdReference.current = postId;
    }
    await setIsOpen(null, { scroll: false, shallow: true });
    await setPostId(null, { scroll: false, shallow: true });
    await setOpenPostViewerEventSource(null, { scroll: false, shallow: true });
  };

  const setCurrentPostViewerId = async (postId: string): Promise<void> => {
    await setPostId(postId, { scroll: false, shallow: true });
  };

  return {
    currentPostViewerId: postId || undefined,
    closePostViewer,
    isPostViewerOpen: !clientHasRendered ? false : isOpen === 'true',
    openPostViewer,
    openPostViewerEventSource,
    setCurrentPostViewerId,
  };
};
